/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.0.1
 */

import { FC, useCallback } from 'react';

import { LinkBlock } from '../../../../../../components/common';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { IPageDashboardTranslation } from '../../../../../../modules/internationalization/types';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import { openExternalRoute } from '../../../../../../routes/constants/externalRoutes';
import { getPagePaymentsUrl, isPagePaymentsExternalUrl } from '../../../../../PagePayments/utils/getPaymentsUrl';
import { WidgetListStyled } from '../../styled/WidgetStyled';
import { WidgetPaymentHistorySuccessStyled } from '../styled/widgetPaymentHistoryStyled';
import { IPaymentsHistoryItem } from '../widgetPaymentHistoryTypes';
import WidgetPaymentHistorySuccessList from './WidgetPaymentHistorySuccessList';

interface IWidgetPaymentHistorySuccessfulProps {
    accountId: number;
    componentsFeature: guardComponentType;
    payments: IPaymentsHistoryItem[];
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: IPageDashboardTranslation & ICommonTranslation;
}

const WidgetPaymentHistorySuccessful: FC<IWidgetPaymentHistorySuccessfulProps> = props => {
    const { componentsFeature, payments, pushGtmAnalytic, translation, accountId } = props;

    const onClickLink = useCallback((): void => {
        pushGtmAnalytic({
            operation: GTMOperations.widgetHistoryPayment.clickLinkAllPaymentsWidgetHistoryPayments,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        if (isPagePaymentsExternalUrl(componentsFeature)) {
            openExternalRoute(getPagePaymentsUrl(componentsFeature, accountId?.toString()));
        }
    }, [accountId, pushGtmAnalytic, componentsFeature]);

    return (
        <WidgetPaymentHistorySuccessStyled>
            <div className="widget-payment-history__list">
                <WidgetListStyled>
                    <WidgetPaymentHistorySuccessList
                        payments={payments}
                        translation={translation}
                    />
                </WidgetListStyled>

            </div>
            <LinkBlock
                dataName="redirectToAccountPayments"
                isExternalUrl={isPagePaymentsExternalUrl(componentsFeature)}
                url={getPagePaymentsUrl(componentsFeature, accountId?.toString())}
                onClick={onClickLink}
            >
                {translation.widgets.paymentHistory.successful.btnText}
            </LinkBlock>
        </WidgetPaymentHistorySuccessStyled>
    );
};

export default WidgetPaymentHistorySuccessful;
